import PT from 'prop-types';
import React__default, { useContext, useMemo, useState, useEffect } from 'react';
import { ThemeContext } from 'styled-components';
import { g as getMatchMediaFunction } from './getMatchMediaFunction-6361be45.js';
import { d as defaultMobileTheme } from '../themes/mobile-4442720f.js';
import { g as getMobileTheme } from './getMobileTheme-814f492a.js';
import { M as MobileMode } from './MobileMode-ef55d00f.js';
import { M as MobileModeChangeSource } from './MobileModeChangeSource-b30fe130.js';
import { M as MobileModeHint } from './MobileModeHint-af8b89c1.js';
import { M as MobileThemeContext } from './MobileThemeContext-15ca3ea1.js';
import { u as useDefaultStateManager } from './useDefaultStateManager-49c964ae.js';
import { i as isMobileMediaQuery } from './isMobileMediaQuery-eaca2555.js';

function MobileThemeProvider(props) {
  const {
    mobileMode = MobileMode.Auto,
    mobileModeHint = MobileModeHint.Mobile,
    onMobileModeChange: externalOnMobileModeChange,
    children
  } = props;
  const defaultTheme = useContext(ThemeContext);

  /* use the mediaQuery from the theme context if it is available */
  const mediaQuery = isMobileMediaQuery(defaultTheme) ? defaultTheme.mobile.mediaQuery : defaultMobileTheme.mobile.mediaQuery;

  /* Create a matchMedia object. Recreate it if the mediaQuery changes */
  const isMobileMatchMedia = useMemo(() => getMatchMediaFunction(mobileModeHint)(mediaQuery), [mediaQuery]);
  const [browserIsMobile, setBrowserIsMobile] = useState(isMobileMatchMedia.matches);
  const {
    isMobile: autoIsMobile,
    onMobileModeChange: defaultOnMobileModeChange
  } = useDefaultStateManager(isMobileMatchMedia.matches);

  /* Use the external state manager when it is available */
  const onMobileModeChange = externalOnMobileModeChange !== null && externalOnMobileModeChange !== void 0 ? externalOnMobileModeChange : defaultOnMobileModeChange;
  useEffect(() => {
    /* setup a listener for browser change its mobile/not-mobile status */
    const isMobileMatchMediaListener = event => {
      /* only fire a onMobileModeChange if mobileMode has changed */
      setBrowserIsMobile(event.matches);
      onMobileModeChange({
        source: MobileModeChangeSource.Browser,
        mobileMode: event.matches ? MobileMode.Enable : MobileMode.Disable
      });
    };
    isMobileMatchMedia.addListener(isMobileMatchMediaListener);

    /* Fire a change event on mount if the hint does not match what the browser detected */
    if (isMobileMatchMedia.matches !== (mobileModeHint === MobileModeHint.Mobile)) {
      setBrowserIsMobile(isMobileMatchMedia.matches);
      isMobileMatchMediaListener({
        matches: isMobileMatchMedia.matches
      });
    }
    return () => {
      /* cleanup match media event listener */
      isMobileMatchMedia.removeListener(isMobileMatchMediaListener);
    };
  }, [isMobileMatchMedia, onMobileModeChange, mobileModeHint]);

  /* Use internal logic to pick mobile/not-mobile in "Auto" mode */
  let isMobile = autoIsMobile;

  /* use the mobileMode if it is set to "Enable" or "Disable" */
  if (mobileMode === MobileMode.Enable) {
    isMobile = true;
  } else if (mobileMode === MobileMode.Disable) {
    isMobile = false;
  }

  /* memoize complex context values to prevent unneeded re-renders */
  const mobileThemeContextValue = useMemo(() => ({
    isMobile,
    onMobileModeChange,
    browserIsMobile
  }), [isMobile, onMobileModeChange, browserIsMobile]);

  /* Create the mobile theme and memoize it */
  const mobileTheme = useMemo(() => {
    if (isMobile === browserIsMobile && mobileMode === MobileMode.Auto) {
      return getMobileTheme(MobileMode.Auto)(defaultTheme);
    }
    return getMobileTheme(isMobile ? MobileMode.Enable : MobileMode.Disable)(defaultTheme);
  }, /* regenerate the theme if incoming theme or mode changes */
  [mobileMode, defaultTheme, isMobile]);
  return /*#__PURE__*/React__default.createElement(ThemeContext.Provider, {
    value: mobileTheme
  }, /*#__PURE__*/React__default.createElement(MobileThemeContext.Provider, {
    value: mobileThemeContextValue
  }, children));
}
MobileThemeProvider.propTypes = {
  mobileMode: PT.oneOf([MobileMode.Auto, MobileMode.Enable, MobileMode.Disable]),
  mobileModeHint: PT.oneOf([MobileModeHint.Mobile, MobileModeHint.NotMobile]),
  onMobileModeChange: PT.func,
  children: PT.node.isRequired
};
MobileThemeProvider.defaultProps = {
  mobileMode: MobileMode.Auto,
  mobileModeHint: MobileModeHint.Mobile,
  onMobileModeChange: undefined
};

export { MobileThemeProvider as M };
//# sourceMappingURL=MobileThemeProvider-8985dc5c.js.map
